import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage } from 'react-intl';
import { generatePath, useParams } from 'react-router';

import { Position, Sport } from '@sorare/core/src/__generated__/globalTypes';
import defaultShield from '@sorare/core/src/assets/club/shield_none.webp';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { Header } from '@sorare/core/src/components/seo/Header';
import { OfficialProduct } from '@sorare/core/src/components/seo/OfficialProduct';
import { Competitions } from '@sorare/core/src/components/seo/Team/Competitions';
import { Departures } from '@sorare/core/src/components/seo/Team/Departures';
import { Manager } from '@sorare/core/src/components/seo/Team/Manager';
import { MoreAboutTeam } from '@sorare/core/src/components/seo/Team/MoreAboutTeam';
import { RelatedClubs } from '@sorare/core/src/components/seo/Team/RelatedClubs';
import { Standings } from '@sorare/core/src/components/seo/Team/Standings';
import {
  FOOTBALL_CLUBS_SLUG,
  FOOTBALL_COUNTRIES_COUNTRYCODE,
  FOOTBALL_LEAGUES_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { Last11 } from '#src/components/seo/Last11/index.tsx';

import { GamesNavigator } from './GamesNavigator/index.tsx';
import type {
  FootballClubOverviewQuery,
  FootballClubOverviewQueryVariables,
} from './__generated__/index.graphql.ts';

const FOOTBALL_CLUB_OVERVIEW_QUERY = gql`
  query FootballClubOverviewQuery($slug: String!, $seasonStartYear: Int!) {
    football {
      club(slug: $slug) {
        slug
        name
        code
        pictureSecondaryUrl
        founded
        styleGuideColor
        domesticLeague {
          slug
          displayName
          pictureUrl
          released
          stages(seasonStartYear: $seasonStartYear) {
            id
            name
            groups {
              id
              name
              contestants {
                id
                ... on FootballContestantType {
                  id
                  matchesDrawn
                  matchesLost
                  matchesPlayed
                  matchesWon
                  goalsFor
                  goalsAgainst
                  points
                  rank
                  rankStatus
                }
                team {
                  slug
                  name
                  pictureSecondaryUrl
                }
              }
            }
          }
        }
        domesticLeagueRanking
        country {
          slug
          name
        }
        dataPartner {
          slug
          ...Header_dataPartner
        }
        latestGames(first: 1) {
          nodes {
            id
          }
        }
        ...Departures_TeamInterface
        activeCompetitions {
          slug
          displayName
          pictureUrl
          released
        }
        relatedClubs {
          slug
          name
          pictureSecondaryUrl
          domesticLeague {
            slug
            displayName
          }
        }
        ...Header_subscription
      }
    }
  }
  ${Header.fragments.dataPartner}
  ${Header.fragments.subscription}
  ${Departures.fragments.TeamInterface}
` as TypedDocumentNode<
  FootballClubOverviewQuery,
  FootballClubOverviewQueryVariables
>;

export const Overview = ({ seasonStartYear }: { seasonStartYear?: number }) => {
  const { slug } = useParams();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');

  const { data, loading } = useQuery(FOOTBALL_CLUB_OVERVIEW_QUERY, {
    variables: { slug: slug || '', seasonStartYear: seasonStartYear || 0 },
    skip: !slug,
  });

  useTitleAndDescription(
    metadatas.clubOverview.title,
    metadatas.clubOverview.description,
    {
      club: data?.football.club.name,
    },
    {
      img: data?.football.club.pictureSecondaryUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { club } = data.football;

  const coach = club.activeMemberships.nodes.find(
    membership => membership.player.position === Position.Coach
  );

  const releasedActiveCompetitions = club.activeCompetitions.filter(
    competition => competition.released
  );

  const blocks = {
    header: (
      <>
        <Header
          id={club.slug}
          variant="team"
          displayName={club.name}
          color={club.styleGuideColor}
          pictureUrl={club.pictureSecondaryUrl || defaultShield}
          memberships={[
            {
              name: club.country.name,
              pictureUrl: `${FRONTEND_ASSET_HOST}/flags/${club.country.slug}.svg`,
              to: generatePath(FOOTBALL_COUNTRIES_COUNTRYCODE, {
                countryCode: club.country.slug,
              }),
            },
            ...(club.domesticLeague
              ? [
                  {
                    name: club.domesticLeague.displayName,
                    pictureUrl: club.domesticLeague.pictureUrl,
                    to: generatePath(FOOTBALL_LEAGUES_SLUG, {
                      slug: club.domesticLeague.slug,
                    }),
                    verified: club.domesticLeague.released,
                  },
                ]
              : []),
          ]}
          subscription={club}
          dataPartner={club.dataPartner}
        />
        <Helmet
          script={[
            {
              type: 'application/ld+json',
              innerHTML: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'SportsTeam',
                name: club.name,
                mainEntityOfPage: generatePath(FOOTBALL_CLUBS_SLUG, {
                  slug: club.slug,
                }),
                sport: Sport.FOOTBALL,
                image: club.pictureSecondaryUrl,
                ...(coach
                  ? {
                      coach: {
                        '@type': 'Person',
                        name: coach.player.displayName,
                      },
                    }
                  : {}),
              }),
            },
          ]}
        />
      </>
    ),
    standings: club.domesticLeague?.stages.length && (
      <Standings
        displayName={club.name}
        stats={['played', 'won', 'drawn', 'lost', 'gf', 'ga', 'gd', 'points']}
        widths={{
          played: 5,
          won: 5,
          drawn: 5,
          lost: 5,
          gf: 5,
          ga: 5,
          gd: 5,
          points: 5,
        }}
        headers={{
          played: <FormattedMessage {...playerDetails.played} />,
          won: <FormattedMessage {...playerDetails.won} />,
          drawn: <FormattedMessage {...playerDetails.drawn} />,
          lost: <FormattedMessage {...playerDetails.lost} />,
          gf: <FormattedMessage {...playerDetails.goalsFor} />,
          ga: <FormattedMessage {...playerDetails.goalsAgainst} />,
          gd: <FormattedMessage {...playerDetails.goalsDiff} />,
          points: <FormattedMessage {...playerDetails.points} />,
        }}
        groups={club.domesticLeague.stages.flatMap(stage => {
          return stage.groups.map(group => ({
            title: stage.groups.length > 1 ? group.name : null,
            standings: group.contestants
              .map(contestant => {
                if (!isType(contestant, 'FootballContestantType')) return null;

                return {
                  slug: contestant.team!.slug,
                  name: contestant.team!.name,
                  pictureUrl: contestant.team!.pictureSecondaryUrl,
                  to: generatePath(FOOTBALL_CLUBS_SLUG, {
                    slug: contestant.team!.slug,
                  }),
                  rank: contestant.rank || 0,
                  rankStatus: contestant.rankStatus,
                  stats: {
                    played: contestant.matchesPlayed,
                    won: contestant.matchesWon,
                    drawn: contestant.matchesDrawn,
                    lost: contestant.matchesLost,
                    gf: contestant.goalsFor || 0,
                    ga: contestant.goalsAgainst || 0,
                    gd:
                      (contestant.goalsFor || 0) -
                      (contestant.goalsAgainst || 0),
                    points: contestant.points,
                  },
                };
              })
              .filter(Boolean),
          }));
        })}
        highlightedSlug={club.slug}
        nbItemsToShow={5}
      />
    ),
    fixtures: data.football.club.latestGames.nodes[0] && (
      <GamesNavigator displayName={club.name} clubSlug={club.slug} />
    ),
    last11: data.football.club.latestGames.nodes[0] && (
      <Last11
        gameId={data.football.club.latestGames.nodes[0].id}
        slug={club.slug}
      />
    ),
    moreAbout: (
      <MoreAboutTeam
        displayName={club.name}
        manager={coach?.player.displayName}
        founded={club.founded}
      />
    ),
    manager: !!coach && (
      <Manager
        displayName={club.name}
        manager={{
          displayName: coach.player.displayName,
          pictureUrl: coach.player.avatarPictureUrl,
          startDate: coach.startDate,
          country: coach.player.country,
        }}
      />
    ),
    transfers: (
      <>{!!data.football.club && <Departures team={data.football.club} />}</>
    ),
    competitions: releasedActiveCompetitions.length ? (
      <Competitions
        competitions={releasedActiveCompetitions.map(competition => ({
          ...competition,
          name: competition.displayName,
          to: generatePath(FOOTBALL_LEAGUES_SLUG, { slug: competition.slug }),
          rank:
            competition.slug === club.domesticLeague?.slug
              ? club.domesticLeagueRanking
              : undefined,
        }))}
      />
    ) : null,
    relatedClubs: !!club.relatedClubs?.length && (
      <RelatedClubs
        clubs={
          club.relatedClubs.map(relatedClub => ({
            ...relatedClub,
            pictureUrl: relatedClub.pictureSecondaryUrl,
            to: generatePath(FOOTBALL_CLUBS_SLUG, { slug: relatedClub.slug }),
            ...(relatedClub.domesticLeague && {
              competition: {
                ...relatedClub.domesticLeague,
                name: relatedClub.domesticLeague.displayName,
                to: generatePath(FOOTBALL_LEAGUES_SLUG, {
                  slug: relatedClub.domesticLeague.slug,
                }),
              },
            }),
          })) || []
        }
      />
    ),
    officialProduct: club.domesticLeague?.released && (
      <OfficialProduct
        displayName={club.domesticLeague.displayName}
        pictureUrl={club.domesticLeague.pictureUrl}
        to={generatePath(FOOTBALL_LEAGUES_SLUG, {
          slug: club.domesticLeague.slug,
        })}
      />
    ),
  };

  if (isLaptopAndAbove) {
    return (
      <MainAsideLayout
        header={blocks.header}
        aside={[
          blocks.transfers,
          blocks.competitions,
          blocks.manager,
          blocks.relatedClubs,
          blocks.officialProduct,
        ]}
      >
        {blocks.standings}
        {blocks.fixtures}
        {blocks.last11}
        {blocks.moreAbout}
      </MainAsideLayout>
    );
  }

  return (
    <Section>
      {blocks.header}
      {blocks.standings}
      {blocks.fixtures}
      {blocks.last11}
      {blocks.transfers}
      {blocks.competitions}
      {blocks.manager}
      {blocks.relatedClubs}
      {blocks.moreAbout}
    </Section>
  );
};
